"use client"

import { useCallback, useRef, useState } from "react"
import Image, { type ImageProps } from "next/image"

import { cn, getPlaceHolderDishImage } from "@/lib/utils"

interface ZoomableImageProps
  extends Omit<ImageProps, "onMouseMove" | "onMouseLeave" | "className"> {
  className?: string
  zoomScale?: number
}

export function ZoomableImage({
  className,
  zoomScale = 1.2,
  alt = "",
  ...props
}: ZoomableImageProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const animationRef = useRef<number | null>(null)

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current || !imageRef.current) return

      // Cancel any pending animation frame
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current)
      }

      // Use requestAnimationFrame for smooth updates
      animationRef.current = requestAnimationFrame(() => {
        if (!containerRef.current || !imageRef.current) return

        // Clear any CSS transitions for immediate response during hover
        imageRef.current.style.transition = ""

        const rect = containerRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top

        // Calculate the percentage position of the mouse with bounds checking
        const xPercent = Math.max(0, Math.min(100, (x / rect.width) * 100))
        const yPercent = Math.max(0, Math.min(100, (y / rect.height) * 100))

        // Apply transform directly to avoid React state updates
        imageRef.current.style.transformOrigin = `${xPercent}% ${yPercent}%`
        imageRef.current.style.transform = `scale(${zoomScale})`
      })
    },
    [zoomScale]
  )

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)

    // Cancel any pending animation frame
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current)
    }

    // Apply smooth transition back to center with CSS
    if (imageRef.current) {
      imageRef.current.style.transition =
        "transform 0.3s ease-out, transform-origin 0.3s ease-out"
      imageRef.current.style.transformOrigin = "center"
      imageRef.current.style.transform = "scale(1)"

      // Remove transition after animation completes to avoid interfering with hover effects
      setTimeout(() => {
        if (imageRef.current) {
          imageRef.current.style.transition = ""
        }
      }, 300)
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className={cn("relative overflow-hidden cursor-zoom-in", className)}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Image
        {...props}
        alt={alt}
        ref={imageRef as any}
        className="w-full h-full object-cover will-change-transform"
        style={{
          transform: "scale(1)",
          transformOrigin: "center",
        }}
        placeholder="blur"
        blurDataURL={getPlaceHolderDishImage()}
      />
    </div>
  )
}
